import * as React from 'react';
import {FormControlLabel, FormLabel, FormControl, FormGroup, FormHelperText, Box, Checkbox as CheckboxEl} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useMemo, memo, useCallback } from 'react';

interface CheckboxProps {
    onChange: (value: string[]) => void;
    data: string[];
    label?: string;
    description?: string;
    required?: boolean;
    error?: string;
    value: string[];
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
}

const Checkbox = memo((props: CheckboxProps) => {
    const {
        onChange,
        data,
        label,
        description,
        required = false,
        error,
        value = [],
        enabled = true,
        visible = true,
        appValidated = false
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && value.length === 0,
        [required, appHasBeenValidated, value]
    );

    const showError = useMemo(() =>
            error !== undefined && error !== "",
        [error]
    );

    const hasError = showError || showRequiredError;
    const errorMessage = showRequiredError ? 'This field is required' : error;

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    // Memoize the change handler for each checkbox
    const handleCheckboxChange = useCallback((el: string) => {
        const currentValues = Array.isArray(value) ? [...value] : [];
        const newValues = currentValues.includes(el)
            ? currentValues.filter(v => v !== el)
            : [...currentValues, el];

        onChange(newValues);
    }, [value, onChange]);

    // Memoize checked state check
    const isChecked = useCallback((el: string) => {
        return Array.isArray(value) && value.includes(el);
    }, [value]);

    // Memoize checkbox items to prevent recreation on every render
    const checkboxItems = useMemo(() =>
            data.map((el: string) => (
                <FormControlLabel
                    key={el}
                    control={
                        <CheckboxEl
                            checked={isChecked(el)}
                            onChange={() => handleCheckboxChange(el)}
                            name={el}
                            value={el}
                            size="small"
                            color="secondary"
                            disabled={!enabled}
                            icon={<CheckBoxOutlineBlankIcon color="disabled" fontSize="small" />}
                        />
                    }
                    label={el}
                />
            )),
        [data, isChecked, handleCheckboxChange, enabled]
    );

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control">
                <FormLabel required={required} className="form-separate-label">
                    {label}
                </FormLabel>
                <FormGroup>
                    {checkboxItems}
                </FormGroup>
                {showDescription && (
                    <FormHelperText>{description}</FormHelperText>
                )}
                {hasError && (
                    <FormHelperText error={true}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
